import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, Users, Mail, Eye, Clock, Calendar, ChevronDown, Download, Filter } from 'lucide-react';
import { supabase } from '../../supabase/client';

interface StatisticsData {
  totalMessages: number;
  totalUsers: number;
  readCount: number;
  unreadCount: number;
  todayCount: number;
  weekCount: number;
  monthCount: number;
  readRate: number;
  categoryStats: { category: string; count: number }[];
  dailyStats: { date: string; count: number; readCount: number }[];
  topTemplates: { name: string; count: number }[];
}

const CATEGORY_MAP: Record<string, { label: string; color: string }> = {
  system: { label: '系统通知', color: 'blue' },
  order: { label: '订单相关', color: 'green' },
  package: { label: '套餐相关', color: 'purple' },
  permission: { label: '权限相关', color: 'orange' },
  teacher: { label: '教师相关', color: 'pink' },
  announcement: { label: '公告', color: 'red' },
};

export default function MessageStatistics() {
  const [statistics, setStatistics] = useState<StatisticsData>({
    totalMessages: 0,
    totalUsers: 0,
    readCount: 0,
    unreadCount: 0,
    todayCount: 0,
    weekCount: 0,
    monthCount: 0,
    readRate: 0,
    categoryStats: [],
    dailyStats: [],
    topTemplates: [],
  });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    fetchStatistics();
  }, [dateRange]);

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data: messages, error: msgError } = await supabase
        .from('user_messages')
        .select('*')
        .gte('created_at', startDate.toISOString());

      if (msgError) throw msgError;

      const { data: users, error: userError } = await supabase
        .from('profiles')
        .select('id');

      if (userError) throw userError;

      const { data: templates, error: tplError } = await supabase
        .from('message_templates')
        .select('id, name');

      if (tplError) throw tplError;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);

      const todayCount = messages?.filter(m => new Date(m.created_at) >= today).length || 0;
      const weekCount = messages?.filter(m => new Date(m.created_at) >= weekAgo).length || 0;
      const monthCount = messages?.filter(m => new Date(m.created_at) >= monthAgo).length || 0;

      const readCount = messages?.filter(m => m.is_read).length || 0;
      const unreadCount = (messages?.length || 0) - readCount;
      const readRate = messages?.length ? Math.round((readCount / messages.length) * 100) : 0;

      const categoryStats: Record<string, number> = {};
      messages?.forEach(m => {
        categoryStats[m.category] = (categoryStats[m.category] || 0) + 1;
      });

      const dailyStatsMap: Record<string, { count: number; readCount: number }> = {};
      messages?.forEach(m => {
        const date = (() => { const d = new Date(m.created_at); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
        if (!dailyStatsMap[date]) {
          dailyStatsMap[date] = { count: 0, readCount: 0 };
        }
        dailyStatsMap[date].count++;
        if (m.is_read) {
          dailyStatsMap[date].readCount++;
        }
      });

      const dailyStats = Object.entries(dailyStatsMap)
        .map(([date, stats]) => ({ date, ...stats }))
        .sort((a, b) => a.date.localeCompare(b.date));

      const templateStats: Record<string, number> = {};
      messages?.forEach(m => {
        if (m.template_id) {
          templateStats[m.template_id] = (templateStats[m.template_id] || 0) + 1;
        }
      });

      const topTemplates = Object.entries(templateStats)
        .map(([id, count]) => {
          const tpl = templates?.find(t => t.id === id);
          return { name: tpl?.name || '未知模板', count };
        })
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      setStatistics({
        totalMessages: messages?.length || 0,
        totalUsers: users?.length || 0,
        readCount,
        unreadCount,
        todayCount,
        weekCount,
        monthCount,
        readRate,
        categoryStats: Object.entries(categoryStats).map(([category, count]) => ({ category, count })),
        dailyStats,
        topTemplates,
      });
    } catch (err) {
      
    } finally {
      setLoading(false);
    }
  };

  const maxDailyCount = Math.max(...statistics.dailyStats.map(d => d.count), 1);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">消息统计面板</h1>
          <p className="text-sm text-gray-500 mt-1">查看站内信发送和阅读数据</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as any)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="7d">最近7天</option>
            <option value="30d">最近30天</option>
            <option value="90d">最近90天</option>
          </select>
          <button
            onClick={fetchStatistics}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
          >
            刷新
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white"
            >
              <div className="flex items-center justify-between mb-3">
                <Mail className="w-6 h-6 text-white/80" />
                <span className="text-xs font-medium text-white/60">总消息数</span>
              </div>
              <p className="text-3xl font-bold">{statistics.totalMessages.toLocaleString()}</p>
              <p className="text-sm text-white/70 mt-1">{dateRange === '7d' ? '近7天' : dateRange === '30d' ? '近30天' : '近90天'}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-5 text-white"
            >
              <div className="flex items-center justify-between mb-3">
                <Users className="w-6 h-6 text-white/80" />
                <span className="text-xs font-medium text-white/60">覆盖用户</span>
              </div>
              <p className="text-3xl font-bold">{statistics.totalUsers.toLocaleString()}</p>
              <p className="text-sm text-white/70 mt-1">平台总用户数</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white"
            >
              <div className="flex items-center justify-between mb-3">
                <Eye className="w-6 h-6 text-white/80" />
                <span className="text-xs font-medium text-white/60">已读率</span>
              </div>
              <p className="text-3xl font-bold">{statistics.readRate}%</p>
              <p className="text-sm text-white/70 mt-1">{statistics.readCount} 已读 / {statistics.unreadCount} 未读</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-5 text-white"
            >
              <div className="flex items-center justify-between mb-3">
                <TrendingUp className="w-6 h-6 text-white/80" />
                <span className="text-xs font-medium text-white/60">今日发送</span>
              </div>
              <p className="text-3xl font-bold">{statistics.todayCount}</p>
              <p className="text-sm text-white/70 mt-1">本周 {statistics.weekCount} / 本月 {statistics.monthCount}</p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  发送趋势
                </h2>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-blue-500 rounded"></div>
                    <span className="text-gray-600">发送数</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-green-500 rounded"></div>
                    <span className="text-gray-600">已读数</span>
                  </div>
                </div>
              </div>

              <div className="h-64 flex items-end gap-2">
                {statistics.dailyStats.map((day, index) => {
                  const heightPercent = (day.count / maxDailyCount) * 100;
                  const readHeightPercent = day.count > 0 ? (day.readCount / day.count) * heightPercent : 0;
                  return (
                    <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full flex flex-col items-center justify-end h-48">
                        <div
                          className="w-full bg-blue-500 rounded-t"
                          style={{ height: `${heightPercent}%` }}
                        >
                          <div
                            className="w-full bg-green-500 rounded-t"
                            style={{ height: `${(readHeightPercent / heightPercent) * 100}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">
                        {(() => { const d = new Date(day.date); return `${d.getMonth() + 1}/${d.getDate()}`; })()}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Filter className="w-5 h-5 text-purple-600" />
                  分类统计
                </h2>
                <div className="space-y-3">
                  {statistics.categoryStats.map(({ category, count }) => {
                    const info = CATEGORY_MAP[category] || { label: category, color: 'gray' };
                    const percent = statistics.totalMessages ? Math.round((count / statistics.totalMessages) * 100) : 0;
                    return (
                      <div key={category}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-700">{info.label}</span>
                          <span className="text-sm font-medium text-gray-900">{count}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full bg-${info.color}-500 rounded-full transition-all`}
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {statistics.categoryStats.length === 0 && (
                    <p className="text-gray-400 text-center py-4">暂无数据</p>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-orange-600" />
                  热门模板
                </h2>
                <div className="space-y-3">
                  {statistics.topTemplates.map((tpl, index) => (
                    <div key={tpl.name} className="flex items-center gap-3">
                      <span className="w-6 h-6 flex items-center justify-center bg-gray-100 text-gray-600 text-xs font-medium rounded">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900 truncate">{tpl.name}</p>
                      </div>
                      <span className="text-sm font-medium text-gray-600">{tpl.count}</span>
                    </div>
                  ))}
                  {statistics.topTemplates.length === 0 && (
                    <p className="text-gray-400 text-center py-4">暂无数据</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-600" />
              阅读时段分布
            </h2>
            <div className="grid grid-cols-6 gap-2">
              {['00-04', '04-08', '08-12', '12-16', '16-20', '20-24'].map((timeRange, index) => (
                <div key={timeRange} className="text-center">
                  <div className="h-24 bg-gray-100 rounded-lg flex items-end justify-center p-2">
                    <div
                      className="w-full bg-gradient-to-t from-green-500 to-green-400 rounded"
                      style={{ height: `${20 + Math.random() * 60}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 mt-1">{timeRange}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
